import { ActivatedRoute } from '@angular/router';
import { AsyncPipe, DecimalPipe } from '@angular/common';
import { Store } from '@ngrx/store';
import {
  Component,
  OnInit,
  OnDestroy,
  ChangeDetectionStrategy,
  output,
  inject,
  input,
} from '@angular/core';
import { Observable, BehaviorSubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import type { PaymentMethod, TENANTSINTERFACE } from '@fitness-force/models';
import {
  getPaymentMethodDetails,
  PaymentMethodDisplayData,
  FeatureFlagService,
} from '@fitness-force/shared-utils';

import { DividerModule } from 'primeng/divider';
import { TimelineModule } from 'primeng/timeline';
import { TableModule } from 'primeng/table';
import {
  UntypedFormBuilder,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { RippleModule } from 'primeng/ripple';
import { SelectModule } from 'primeng/select';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';
import { StyleClassModule } from 'primeng/styleclass';
import { ToastMessageService, ClientService } from '@fitness-force/api';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { DialogModule } from 'primeng/dialog';
import { InvoiceTabComponent } from './invoice-tab/invoice-tab.component';
import { PaymentTabComponent } from './payment-tab/payment-tab.component';
import { TENANTS_INFO } from '@fitness-force/state';
import { DateTimeFormatPipe } from '@fitness-force/pipes/datetime-format';
import { SalesReturnComponent } from '@fitness-force/sales-return';
import { RefundListComponent } from '@fitness-force/refund';
import { RetailProductsComponent } from '@fitness-force/retail-billing';
import { FreezeListTabComponent } from '@fitness-force/freeze-lib';
import { GuestPassListTabComponent } from './guest-pass-list-tab/guest-pass-list-tab.component';

@Component({
  selector: 'transaction-tab',
  templateUrl: './transaction-tab.component.html',
  styleUrls: ['./transaction-tab.component.scss'],
  providers: [],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    DividerModule,
    TimelineModule,
    TableModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    DateTimeFormatPipe,
    InvoiceTabComponent,
    PaymentTabComponent,
    SalesReturnComponent,
    RefundListComponent,
    RetailProductsComponent,
    FreezeListTabComponent,
    GuestPassListTabComponent,
    TranslateModule,
    AsyncPipe,
    DecimalPipe,
  ],
})
export class TransactionTabComponent implements OnInit, OnDestroy {
  private readonly store = inject(Store);
  private readonly activatedRouter = inject(ActivatedRoute);
  private readonly clientService = inject(ClientService);
  private readonly toastMessageService = inject(ToastMessageService);
  private readonly fb = inject(UntypedFormBuilder);
  private readonly featureFlagService = inject(FeatureFlagService);
  private readonly translate = inject(TranslateService);

  getAccessTenantList$: Observable<any> | any | null;
  tenantId = 0;
  paymentStatus: any[] = [];
  invoicesPaidFullTableData: any[] = [];
  readonly clientId = input<number>(0);
  readonly activeSubTab = input<string>();
  localClientId: number = 0;
  private readonly destroy$ = new Subject<void>();
  paymentMethods$ = new BehaviorSubject<PaymentMethod[]>([]);
  showBulkActionBar = false;
  transactionEnable = false;
  showPopUpForNewCard = false;

  showoptions = false;
  showEmailToast = false;
  showVoidPaymentModal = false;

  tags: string[] | undefined;

  activeTab = 'Transaction';
  showSalesReturn = new BehaviorSubject(false);
  showRefund = new BehaviorSubject(false);
  showRetail = new BehaviorSubject(false);

  readonly updateData = output<boolean>();

  voidPaymentForm: any;
  paymentDetails: any = {};

  constructor() {
    this.tags = ['New Member', 'High Value', 'Allergy'];
    this.voidPaymentForm = this.fb.group({
      void_reason: [''],
    });
  }

  ngOnInit(): void {
    // Use getFlag$() observable to wait for Statsig initialization
    this.featureFlagService
      .getFlag$('show_salesreturn')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showSalesReturn.next(isEnabled);
      });

    this.featureFlagService
      .getFlag$('show_refund')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showRefund.next(isEnabled);
      });

    this.featureFlagService
      .getFlag$('show_retail')
      .pipe(takeUntil(this.destroy$))
      .subscribe((isEnabled) => {
        this.showRetail.next(isEnabled);
      });

    this.componentInit();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  componentInit() {
    this.getAccessTenantList$ = this.store.select(TENANTS_INFO);
    this.getAccessTenantList$.subscribe((tenant: TENANTSINTERFACE) => {
      this.tenantId = tenant.selected_tenant.id;
      if (this.tenantId > 0) {
        this.store.dispatch({ type: 'ROUTER_GO' });
      }
    });
    this.getCLientDetails();
  }

  getCLientDetails() {
    this.localClientId = this.activatedRouter.snapshot?.params['clientid']
      ? parseInt(this.activatedRouter?.snapshot?.params['clientid'])
      : this.clientId();
    this.clientService.getClientPaymentMethods(this.clientId()).subscribe({
      next: (res: PaymentMethod[]) => {
        this.paymentMethods$.next(res ? res : []);
      },
      error: (error) => {
        this.toastMessageService.showError(error);
      },
    });
  }

  formatDate(d: any) {
    const parseDate = `${d.getFullYear()}-${
      d.getMonth() > 9 ? d.getMonth() : d.getMonth() + 1
    }-${d.getDate()}`;
    return parseDate.toString();
  }

  handleEvent(e: any) {
    this.componentInit();
  }

  handleTabAction(event: { closePopup: boolean; reloadData: boolean }) {
    if (event.reloadData) this.componentInit();
  }

  handleUpdateData(event: any) {
    if (event?.action && event.action == 'updated') this.updateData.emit(true);
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  getExpirtDateError(expirtDate: string) {
    const expiry = new Date(expirtDate);
    const todayDate = new Date();
    const priorDate = new Date(new Date().setDate(expiry.getDate() - 30));
    return todayDate <= priorDate;
  }
  shareLink(type: string) {
    // here will have api call for sms and email.
  }

  showPaymentMethod(event: boolean) {
    this.transactionEnable = event;
    this.getCLientDetails();
  }

  handleFreezeUpdateData(event: any) {
    if (event?.action && event.action == 'updated') this.updateData.emit(true);
  }

  getPaymentMethodDetails(
    paymentMethod: PaymentMethod,
  ): PaymentMethodDisplayData {
    return getPaymentMethodDetails(paymentMethod, this.translate);
  }

  deletePayment() {
    // Implementation for delete payment
    this.showVoidPaymentModal = false;
  }
}
